/* eslint-disable default-case */
/* eslint-disable no-await-in-loop */
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Yup from "yup";
import { useFormikContext } from "formik";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, SideBarDef } from "ag-grid-community";
import { useLocation, useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import _ from "lodash";
import {
  Box,
  ButtonCancelSecondary,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../components/utils";
import {
  NameAndId,
  OfferFilterV2,
  TransactionDetailsData,
} from "../../../types/Transaction";
import { ChvronDown } from "../../../utils/assets/chevronDown";
import FilterOptions from "./utils/FilterOptions";
import {
  formatCurrency,
  getCurrencyType,
} from "../../../utils/currencyFormatter";
import Avatar from "../../esopOverview/Avatar";
import {
  useCreateExerciseTransaction,
  useEditTransaction,
} from "../../../queries/transactionV2";
import { MonetaryEventState } from "../../../types/OptionBuyback";
import GenericTableHeader from "../../../shared/GenericTableHeader";
import SearchInput from "../../../components/shared/SearchInput";
import StatusLabelColors from "../../../constants/StatusLabelColors";
import { deleteTransactionDetails } from "../../../api/TransactionApisV2";

interface PreviewDetailsProps {
  validationSchemas: Yup.ObjectSchema<any>[];
  onStepChange: (e?: number) => void;
  onBackClick?: () => void;
}

const PreviewExerciseDetails = (props: PreviewDetailsProps) => {
  const currency = getCurrencyType();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { id, mode: paramsMode } = params;
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || paramsMode;
  const { mutate: createTransaction } = useCreateExerciseTransaction();
  const { mutate: editTransaction } = useEditTransaction();
  const gridApi = useRef<GridApi | any>(null);
  const onGridReady = (params: any) => {
    gridApi.current = params;
  };
  const formik = useFormikContext<TransactionDetailsData>();

  const [data, setData] = useState({
    optionsExercised: 0,
    totalExercisePrice: 0,
    totalTaxPayable: 0,
  });
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isStartingExercise, setIsStartingExercise] = useState(false);

  // Sample data for the grid
  const rowData = useMemo(() => formik.values.previewList || [], []);

  function EmployeeInfoRender(emp: any) {
    return (
      <HStack>
        <Avatar
          name={emp.data.employeeName}
          status={emp.data.employmentStatus}
        />
        <Box className="ml-2">
          <p className="text-xs font-medium text-gray-dark">
            {emp.data.employeeName}
          </p>
          <p className="capitalize text-xxs text-gray-light">
            {emp.data.employeeDepartment || ""} |{" "}
            {emp.data.employeeDesignation || ""}
          </p>
        </Box>
      </HStack>
    );
  }

  function TaxBracketRender(emp: any) {
    const taxDetails = emp.data.transactionCompensationDetails?.taxDetails;
    const salary = emp.data.transactionCompensationDetails?.salary;
    const showSalary = !!salary && Number(salary) > 0;
    if (!showSalary && !taxDetails) {
      return (
        <HStack className="w-full ">
          <Box className="px-3 py-1 text-xs font-semibold text-gray-500 border border-gray-200 rounded bg-gray-50">
            No Details
          </Box>
        </HStack>
      );
    }

    return (
      <VStack className="w-full ">
        {showSalary && (
          <Box className="px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200 rounded bg-blue-50">
            Salary:{" "}
            <span className="font-bold">
              {Number(salary).toLocaleString(currency)}
            </span>
          </Box>
        )}
        {taxDetails && (
          <VStack className="gap-2 ">
            {Number(taxDetails.taxPercentage) > 0 && (
              <Box className="flex items-center gap-1 ">
                <span className="text-xs font-medium text-gray-600">Tax:</span>
                <span className="font-semibold">
                  {taxDetails.taxPercentage * 100}%
                </span>
              </Box>
            )}
            {Number(taxDetails.surchargePercentage) > 0 && (
              <Box className="flex items-center gap-1 ">
                <span className="text-xs font-medium text-gray-600">
                  Surcharge:
                </span>
                <span className="font-semibold ">
                  {taxDetails.surchargePercentage * 100}%
                </span>
              </Box>
            )}
            {Number(taxDetails.cessPercentage) > 0 && (
              <Box className="flex items-center gap-1 ">
                <span className="text-xs font-medium text-gray-600">Cess:</span>
                <span className="font-semibold ">
                  {taxDetails.cessPercentage * 100}%
                </span>
              </Box>
            )}
          </VStack>
        )}
      </VStack>
    );
  }

  function EmployementStatusRender(emp: any) {
    const status = emp.value;
    if (!status || status === "") {
      return <></>;
    }
    const { color, backgroundColor } = StatusLabelColors[status];
    return (
      <HStack>
        <Box className="min-h-4 ">
          <span
            className={`flex items-center capitalize min-w-10 h-6  rounded-sm font-medium text-center text-[9px]  leading-4 px-1`}
            style={{ color, backgroundColor }}
          >
            {status}
          </span>
        </Box>
      </HStack>
    );
  }

  useEffect(() => {
    const optionsExercised = rowData.reduce(
      (sum: number, employee: any) =>
        sum + (employee.totalEligibleOptions || 0),
      0
    );
    const totalExercisePrice = rowData.reduce(
      (sum: number, employee: any) =>
        sum +
        employee.grantRelatedDetails.reduce(
          (innerSum: number, grant: any) =>
            innerSum + (grant.eligibleOptions || 0) * (grant.strikePrice || 0),
          0
        ),
      0
    );
    const totalTaxPayable = rowData.reduce(
      (sum: number, employee: any) =>
        sum + (employee.transactionCompensationDetails?.totalTaxAmount || 0),
      0
    );

    setData({
      optionsExercised,
      totalExercisePrice,
      totalTaxPayable,
    });
  }, [rowData]);

  const columnDefs: ColDef[] = [
    {
      headerName: "EMPLOYEE NAME",
      width: 200,
      autoHeight: true,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      wrapText: true,
      colId: "employeeName",
      valueGetter: (params: any) => ({
        employeeName: params.data?.employeeName,
        employeeDepartment: params.data?.employeeDepartment,
        employeeDesignation: params.data?.employeeDesignation,
        hrId: params.data?.hrId,
      }),
      filter: "agMultiColumnFilter",
      menuTabs: ["filterMenuTab"],
      filterParams: {
        maxNumConditions: 5,
        buttons: ["reset"],
        filters: [
          {
            title: "Name",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeName,
              valueFormatter: (params: any) => params.value?.employeeName,
            },
          },
          {
            title: "Department",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDepartment,
              valueFormatter: (params: any) => params.value?.employeeDepartment,
            },
          },
          {
            title: "Designation",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDesignation,
              valueFormatter: (params: any) =>
                params.value?.employeeDesignation,
            },
          },
          {
            title: "Emp ID",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.hrId,
              valueFormatter: (params: any) => params.value?.hrId,
            },
          },
        ],
      },
      comparator(valueA, valueB) {
        return valueA?.employeeName
          ?.toLowerCase()
          ?.localeCompare(valueB?.employeeName?.toLowerCase());
      },
      minWidth: 200,
      cellRendererParams: (params: any) => ({
        employeeName: params.data.employeeName,
        employeeDesignation: params.data.employeeDesignation,
        employeeDepartment: params.data.employeeDepartment,
        employeeState: params.data.employeeState,
      }),
      cellRenderer: (params: any) =>
        params.data?.isTotalRow ? "Total" : EmployeeInfoRender(params),
      getQuickFilterText: (params: any) =>
        [
          params.data.employeeName,
          params.data.employeeDesignation,
          params.data.employeeDepartment,
          params.data.employeeState,
        ]
          .filter(Boolean)
          .join(" "),
    },
    {
      headerName: "EMP ID",
      autoHeight: true,
      field: "hrId",
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 80,
    },
    {
      headerName: "VESTED OPTIONS",
      autoHeight: true,
      field: "totalVestedOptions",
      filter: "agNumberColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 80,
      valueFormatter: (params) => params.value.toLocaleString(currency),
    },
    {
      headerName: "ELIGIBLITY",
      autoHeight: true,
      filter: "agNumberColumnFilter",
      menuTabs: ["filterMenuTab"],
      valueGetter: (params) => {
        const percentage =
          formik.values.monetaryDetails.percentageOfNetVestedOptions || 0;
        const totalEligibleOptions = params.data.totalEligibleOptions || 0;

        return percentage > 0
          ? Math.floor(totalEligibleOptions * (percentage / 100))
          : totalEligibleOptions;
      },
      valueFormatter: (params) => params.value.toLocaleString(currency),
      flex: 1,
      width: 80,
    },
    {
      headerName: "TAX BRACKET",
      autoHeight: true,
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      cellStyle: { paddingTop: "15px", lineHeight: "0" },

      minWidth: 120,
      cellRendererParams: (params: any) => ({
        taxPercentage:
          params.data.transactionCompensationDetails?.taxDetails?.taxPercentage,
        cessPercentage:
          params.data.transactionCompensationDetails?.taxDetails
            ?.cessPercentage,
        surchargePercentage:
          params.data.transactionCompensationDetails?.taxDetails
            ?.surchargePercentage,
        salary: params.data.transactionCompensationDetails?.salary,
      }),
      cellRenderer: (params: any) =>
        params.data?.isTotalRow ? "" : TaxBracketRender(params),
      getQuickFilterText: (params: any) =>
        [
          params.data.transactionCompensationDetails?.taxDetails?.taxPercentage,
          params.data.transactionCompensationDetails?.taxDetails
            ?.cessPercentage,
          params.data.transactionCompensationDetails?.taxDetails
            ?.surchargePercentage,
          params.data.transactionCompensationDetails?.salary,
        ]
          .filter(Boolean)
          .join(" "),
    },
    {
      headerName: "STATUS",
      autoHeight: true,
      field: "employeeState",
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      minWidth: 80,
      cellRendererParams: ({ value }: { value: any }) => value?.props || "",
      cellStyle: { paddingTop: "15px", lineHeight: "0" },
      cellRenderer: EmployementStatusRender,
    },
    {
      headerName: "NATIONALITY",
      autoHeight: true,
      field: "nationality",
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 100,
      valueFormatter: (params: any) => params.value || "-",
    },
  ];

  // Grid options
  const isValidDate = (date: string) =>
    Number.isNaN(date) &&
    new Date(date).toString() !== "Invalid Date" &&
    !Number.isNaN(Date.parse(date));

  const normaliseValue = (value: string | number) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      if (isValidDate(value)) return new Date(value).valueOf();
      try {
        value = value.replaceAll(",", "");
      } catch (TypeError) {
        value = value.replace(/,./g, "");
      }
      return parseFloat(value);
    }
    return value;
  };

  const sortComparator = (
    valueA: number | string,
    valueB: number | string,
    ..._: any[]
  ) => normaliseValue(valueA) - normaliseValue(valueB);

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      comparator: sortComparator,
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
    }),
    []
  );

  const handleBackClick = () => {
    if (props.onBackClick) {
      props.onBackClick();
    }
  };

  const handlePreviewValidation = async () => {
    try {
      for (let i = 0; i < props.validationSchemas.length; i++) {
        await props.validationSchemas[i].validate(formik.values, {
          abortEarly: false,
        });
      }
    } catch (validationError) {
      if (validationError instanceof Yup.ValidationError) {
        const errors: { [key: string]: string } = {};
        validationError.inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });

        formik.setErrors(errors);

        const firstErrorField = validationError.inner[0]?.path;
        if (firstErrorField) {
          const stepWithError = determineStepFromField(firstErrorField);
          props.onStepChange(stepWithError);
        }
      }
    }
  };

  const determineStepFromField = (fieldName: string): number => {
    if (fieldName.startsWith("step1Field")) {
      return 1;
    } else if (fieldName.startsWith("step2Field")) {
      return 2;
    }
    return 1;
  };

  const filteredRowData = useMemo(
    () =>
      rowData.filter((employee) => {
        if (
          formik.values.offerFilter.selectedEmployeeIds &&
          formik.values.offerFilter.selectedEmployeeIds.length > 0 &&
          formik.values.offerFilter.selectedEmployeeIds
            .map((e: any) => e.id || e.employeeId)
            .includes(employee.employeeId)
        ) {
          return true;
        }
        // Department filter
        if (
          formik.values.offerFilter.department &&
          formik.values.offerFilter.department.length > 0 &&
          !formik.values.offerFilter.department.includes(
            employee.employeeDepartment
          )
        ) {
          return false;
        }

        // Min vested options filter
        if (
          formik.values.offerFilter.vestedOptions &&
          formik.values.offerFilter.vestedOptions.min &&
          (employee.totalVestedOptions || 0) <
            formik.values.offerFilter.vestedOptions.min
        ) {
          return false;
        }
        // Max vested options filter
        if (
          formik.values.offerFilter.vestedOptions &&
          formik.values.offerFilter.vestedOptions.max &&
          (employee.totalVestedOptions || 0) >
            formik.values.offerFilter.vestedOptions.max
        ) {
          return false;
        }

        if (
          formik.values.offerFilter.eligibleOptions &&
          formik.values.offerFilter.eligibleOptions.min &&
          (employee.totalEligibleOptions || 0) <
            formik.values.offerFilter.eligibleOptions.min
        ) {
          return false;
        }
        // Max vested options filter
        if (
          formik.values.offerFilter.eligibleOptions &&
          formik.values.offerFilter.eligibleOptions.max &&
          (employee.totalEligibleOptions || 0) >
            formik.values.offerFilter.eligibleOptions.max
        ) {
          return false;
        }

        if (
          formik.values.offerFilter.employmentType &&
          formik.values.offerFilter.employmentType.length > 0
        ) {
          const employmentTypes = employee.employmentType || [];
          if (
            !formik.values.offerFilter.employmentType.some((type: any) =>
              employmentTypes.includes(type)
            )
          ) {
            return false;
          }
        }

        if (
          formik.values.offerFilter.tenure &&
          ((formik.values.offerFilter.tenure.min || 0) > 0 ||
            (formik.values.offerFilter.tenure.max || Infinity) < Infinity)
        ) {
          if (
            formik.values.offerFilter.tenure.min &&
            employee.tenure < formik.values.offerFilter.tenure.min
          ) {
            return false;
          }
          if (
            formik.values.offerFilter.tenure.max &&
            employee.tenure > formik.values.offerFilter.tenure.max
          ) {
            return false;
          }
        }

        if (
          formik.values.offerFilter.status &&
          formik.values.offerFilter.status.length > 0
        ) {
          const status = employee.employeeState || "";
          if (!formik.values.offerFilter.status.includes(status)) {
            return false;
          }
        }

        if (
          formik.values.offerFilter.planIds &&
          formik.values.offerFilter.planIds.length > 0
        ) {
          const filteredGrants = employee.grantRelatedDetails.filter((grant) =>
            formik.values.offerFilter.planIds
              ?.map((p) => p.id)
              .includes(grant.planId)
          );

          if (filteredGrants.length === 0) {
            return false;
          }

          employee.totalVestedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.vestedOptions || 0),
            0
          );
          employee.totalEligibleOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.eligibleOptions || 0),
            0
          );

          employee.totalGrantedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.grantedOptions || 0),
            0
          );
          employee.totalCashExercisedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.cashExercisedOptions || 0),
            0
          );
          employee.totalStockExercisedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.stockExercisedOptions || 0),
            0
          );

          employee.grantRelatedDetails = filteredGrants;
        }

        if (
          formik.values.offerFilter.companyIds &&
          formik.values.offerFilter.companyIds.length > 0
        ) {
          const filterCompanyIds = formik.values.offerFilter.companyIds.map(
            (c: any) => c.id || c
          );

          if (
            filterCompanyIds.includes(employee.companyId) &&
            !employee.subsidiaryCompanyId
          ) {
            return true;
          } else if (filterCompanyIds.includes(employee.subsidiaryCompanyId)) {
            return true;
          } else return false;
        }

        if (
          formik.values.offerFilter.excludeEmployeeIds &&
          formik.values.offerFilter.excludeEmployeeIds.length > 0
        ) {
          return !formik.values.offerFilter.excludeEmployeeIds
            .map((e) => e.id)
            .includes(employee.employeeId);
        }

        if (
          formik.values.offerFilter.participationConstraints &&
          formik.values.offerFilter.participationConstraints.length > 0
        ) {
          const constraints =
            formik.values.offerFilter.participationConstraints;
          const participatedDates = employee.participatedEventDates || [];

          if (
            !constraints ||
            constraints.length === 0 ||
            !participatedDates ||
            participatedDates.length === 0
          ) {
            return true;
          }

          for (const constraint of constraints) {
            const { interval, intervalUnit, maxFrequency } = constraint;

            const now = new Date();
            const startDate = new Date();

            switch (intervalUnit) {
              case "Days":
                startDate.setDate(now.getDate() - interval);
                break;
              case "Month":
                startDate.setMonth(now.getMonth() - interval);
                break;
              case "Quarter":
                startDate.setMonth(now.getMonth() - interval * 3);
                break;
              case "Year":
                startDate.setFullYear(now.getFullYear() - interval);
                break;
            }

            const participationCount = employee.participatedEventDates.filter(
              (date) => new Date(date) >= startDate && new Date(date) <= now
            ).length;

            if (participationCount >= maxFrequency) {
              return false;
            }
          }
          return true;
        }

        if (
          formik.values.offerFilter.nationality &&
          formik.values.offerFilter.nationality.length > 0 &&
          !formik.values.offerFilter.nationality.includes(
            employee.nationality || ""
          )
        ) {
          return false;
        }

        return true;
      }),

    [rowData, formik.values.offerFilter]
  );

  const handleSubmit = (clickedStartTransaction = false) => {
    if (clickedStartTransaction) {
      setIsStartingExercise(true);
    } else {
      setIsSavingDraft(true);
    }
    handlePreviewValidation();
    if (formik.isValid) {
      const values: TransactionDetailsData = formik.values;
      const isFileUploaded =
        values.monetaryDetails.participantListMethod === "Upload Excel";
      values.monetaryDetails.generateOffers =
        values.monetaryDetails.participantListMethod === "Upload Excel";
      values.monetaryDetails.isFileUploaded = isFileUploaded;
      if (mode === "create") {
        createTransaction(
          { ...values, startTransaction: clickedStartTransaction },
          {
            onSuccess: (response: any) => {
              toast("Transaction started successfully!", {
                type: "success",
                autoClose: 2000,
              });
              deleteTransactionDetails();
              navigate("/options/esopTransactions");
              setIsSavingDraft(false);
              setIsStartingExercise(false);
            },
            onError: (error: any) => {
              toast(error.response?.data?.errorRaw || "An error occurred", {
                type: "error",
                autoClose: 2000,
              });
              setIsSavingDraft(false);
              setIsStartingExercise(false);
            },
          }
        );
      } else if (mode === "edit") {
        editTransaction(
          { ...values, id, startTransaction: clickedStartTransaction },
          {
            onSuccess: (response: any) => {
              toast("Transaction updated successfully!", {
                type: "success",
                autoClose: 2000,
              });
              deleteTransactionDetails();
              navigate("/options/esopTransactions");
              setIsSavingDraft(false);
              setIsStartingExercise(false);
            },
            onError: (error: any) => {
              toast(error, {
                type: "error",
                autoClose: 2000,
              });
              setIsSavingDraft(false);
              setIsStartingExercise(false);
            },
          }
        );
      }
    } else {
      setIsSavingDraft(false);
      setIsStartingExercise(false);
    }
  };

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode;
    const filteredData = data.childrenAfterAggFilter.map(
      (element: any) => element.data
    );

    const totalVestedOptions = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalVestedOptions,
      0
    );
    const totalEligibility = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalEligibleOptions,
      0
    );

    api.setPinnedBottomRowData([
      {
        employeeName: "Total",
        totalVestedOptions,
        totalEligibleOptions: totalEligibility,
        isTotalRow: true,
      },
    ]);
  }

  return (
    <VStack className="w-full pb-4">
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between text-xl font-semibold text-black-501">
          Preview
        </HStack>
        <HStack className="flex text-sm font-medium text-gray-500">
          Review all configurations, eligible participants, and rules you’ve
          applied before publishing the event. This is your final checkpoint to
          ensure everything is accurate and ready for launch.
        </HStack>
      </VStack>
      <div className="w-full px-6 py-8">
        <VStack className=" gap-2 px-6 py-4 border-solid bg-[#E6EFFF] border-[#3699FF] border-[1px] rounded-md">
          <HStack className="flex justify-between text-base font-semibold text-black-501">
            Exercise Outcome Preview -
          </HStack>
          <HStack className="flex text-xs font-medium text-gray-500">
            For this Preview, assumptions are made that all participants will
            provide 100% willingness and that there is 100% acceptance
          </HStack>
          <HStack className="flex justify-between pt-6 text-base font-semibold text-center text-black-501">
            <VStack className="items-center flex-1 ">
              <div className="text-gray-600">Potential Exercised Options</div>
              <div className="text-black">
                {data.optionsExercised.toLocaleString(currency)}
              </div>
            </VStack>
            <VStack className="items-center flex-1">
              <p className="text-gray-600">Total Exercise Price</p>
              <p className="text-black">
                {formatCurrency(data.totalExercisePrice, currency)}
              </p>
            </VStack>
            {!formik.values.monetaryDetails.transactionPaymentEnabled && (
              <VStack className="items-center flex-1">
                <p className="text-gray-600">Total Tax Payable</p>
                <p className="text-black">
                  {formatCurrency(data.totalTaxPayable, currency)}
                </p>
              </VStack>
            )}
          </HStack>
        </VStack>
      </div>
      {formik.values.monetaryDetails.participantListMethod ===
        "Generate By Filters" && (
        <HStack className="w-full gap-4 px-6 py-4 border border-black rounded-md ">
          {
            <div className="flex flex-wrap gap-4">
              <FilterOptions formik={formik} disableUpdate={true} />
            </div>
          }
        </HStack>
      )}
      <HStack className="justify-between px-6 pt-4">
        <GenericTableHeader
          heading={"Employees"}
          subHeading={""}
          customClassName="flex flex-col px-0 lg:justify-start"
          additionalInfo={<>{`${filteredRowData?.length} employees`}</>}
        ></GenericTableHeader>
        <HStack className="items-center gap-4 ">
          <SearchInput
            className=""
            placeholder="Search"
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          />
        </HStack>
      </HStack>
      {/* AG Grid table */}
      <HStack className="justify-between w-full pt-6 pb-6">
        <Box
          style={{
            height: `${
              rowData.length <= 3
                ? "300"
                : rowData.length >= 10
                ? "600"
                : rowData.length * 100
            }px`,
            minWidth: 900,
          }}
          className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
        >
          <AgGridReact
            getRowStyle={(params) => {
              if (params.data?.isTotalRow) {
                return {
                  fontWeight: 600,
                };
              }
              return undefined;
            }}
            onRowDataUpdated={setPinnedBottomRowData}
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            alwaysMultiSort
            onGridReady={onGridReady}
            animateRows={true}
            sideBar={sideBar}
            defaultColDef={defaultColDef}
            rowData={filteredRowData}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            suppressMenuHide={true}
            columnDefs={columnDefs}
            suppressRowTransform={true}
            rowMultiSelectWithClick={true}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          ></AgGridReact>
        </Box>
      </HStack>
      <HStack className="justify-between w-full p-6">
        <ButtonPrimary1 onClick={handleBackClick}>Back</ButtonPrimary1>
        {formik.values.eventState === MonetaryEventState.DRAFT ? (
          <HStack className="gap-4">
            <ButtonPrimary1
              onClick={() => handleSubmit(false)}
              loading={isSavingDraft}
            >
              Save as Draft
            </ButtonPrimary1>
            <ButtonPrimary
              className="text-white bg-orange-500 hover:bg-orange-600"
              onClick={() => handleSubmit(true)}
              loading={isStartingExercise}
            >
              Start Exercise
            </ButtonPrimary>
          </HStack>
        ) : (
          <ButtonPrimary
            className="text-white bg-orange-500 hover:bg-orange-600"
            onClick={() => props.onStepChange()}
          >
            Next
          </ButtonPrimary>
        )}
      </HStack>
    </VStack>
  );
};

export default PreviewExerciseDetails;
